import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Modal,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { caregiverInfoService } from '../../../../services/caregiverInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('CaregiverInput');

type CaregiverInputScreenRouteProp = RouteProp<RootStackParamList, 'CaregiverInput'>;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const CaregiverInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<CaregiverInputScreenRouteProp>();

  const { caregiverData, isEditing } = route.params || {};

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  const [fullName, setFullName] = useState(caregiverData?.fullName || '');
  const [phoneNumber, setPhoneNumber] = useState(caregiverData?.phoneNumber || '');
  const [email, setEmail] = useState(caregiverData?.email || '');
  const [relationship, setRelationship] = useState(caregiverData?.relationship || '');
  const [state, setState] = useState(caregiverData?.state || '');
  const [notes, setNotes] = useState(caregiverData?.notes || '');
  const [saving, setSaving] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedCaregiverId, setSavedCaregiverId] = useState<string | undefined>();
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [showStateModal, setShowStateModal] = useState(false);

  // Set toggle to true if editing and any additional fields have data
  useEffect(() => {
    if (isEditing && caregiverData) {
      const hasAdditionalData = caregiverData.state ||
                               caregiverData.notes;
      setShowAdditionalInfo(!!hasAdditionalData);
    }
  }, [isEditing, caregiverData]);

  const handleSave = async () => {
    // Validation - require at least full name
    if (!fullName.trim()) {
      Alert.alert('Error', 'Please enter the caregiver\'s full name');
      return;
    }

    try {
      setSaving(true);

      const caregiverUpdateData = {
        taskId: 10, // Caregiver task ID
        fullName: fullName.trim(),
        phoneNumber: phoneNumber.trim(),
        email: email.trim(),
        relationship: relationship.trim(),
        state: state.trim(),
        notes: notes.trim(),
      };

      let savedCaregiver;

      if (isEditing && caregiverData?.id) {
        // Update existing record
        savedCaregiver = await caregiverInfoService.updateCaregiverInfo(
          caregiverData.id,
          caregiverUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedCaregiver?.data || savedCaregiver;
        logger.debug('Caregiver updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('CaregiverLockUnlock', { caregiverId: updatedData.id });
      } else {
        // Create new record
        savedCaregiver = await caregiverInfoService.createCaregiverInfo(caregiverUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedCaregiver?.data || savedCaregiver;
        logger.debug('Caregiver saved successfully with ID:', createdData.id);

        // Store the saved ID for the Done button
        setSavedCaregiverId(createdData.id);

        // Clear the form and reset states for Add Another
        setFullName('');
        setPhoneNumber('');
        setEmail('');
        setRelationship('');
        setState('');
        setNotes('');
        setShowAdditionalInfo(false);

        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset saving so buttons are interactive
        setSaving(false);
      }
    } catch (error) {
      logger.error('Error saving caregiver:', error);
      Alert.alert('Error', 'Failed to save caregiver information. Please try again.');
      setSaving(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFullName('');
    setPhoneNumber('');
    setEmail('');
    setRelationship('');
    setState('');
    setNotes('');
    setShowAddAnotherDone(false);
    setShowAdditionalInfo(false);
    setSaving(false);
  };

  const handleDone = async () => {
    setSaving(false);
    logger.debug('[Done Debug] Using ID:', savedCaregiverId);

    // If we don't have a saved ID, get the most recent caregiver entry
    if (!savedCaregiverId) {
      try {
        const response = await caregiverInfoService.getCaregiverInfoByTaskId(10);
        logger.debug('[Done Debug] API response:', response);

        // Handle response format - check if it's wrapped in a success/data object
        const allCaregivers = response?.data || response;
        logger.debug('[Done Debug] Extracted caregiver array:', allCaregivers);

        const mostRecent = allCaregivers && Array.isArray(allCaregivers) && allCaregivers.length > 0 ? allCaregivers[allCaregivers.length - 1] : null;
        if (mostRecent) {
          logger.debug('[Done Debug] Using most recent ID:', mostRecent.id);
          navigation.navigate('CaregiverLockUnlock', { caregiverId: mostRecent.id });
        } else {
          logger.debug('[Done Debug] No caregiver entries found in array:', allCaregivers);
          Alert.alert('Error', 'No caregiver information found. Please create an entry first.');
        }
      } catch (error) {
        logger.error('[Done Debug] Error getting caregiver:', error);
        Alert.alert('Error', 'Failed to get caregiver information.');
      }
    } else {
      navigation.navigate('CaregiverLockUnlock', { caregiverId: savedCaregiverId });
    }
  };

  const handleStateSelect = (selectedState: string) => {
    setState(selectedState);
    setShowStateModal(false);
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Caregiver Information
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Caregiver Full Name *</Text>
            <TextInput
              style={styles.input}
              value={fullName}
              onChangeText={setFullName}
              placeholder="Enter Full Name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Relationship to Caregiver</Text>
            <TextInput
              style={styles.input}
              value={relationship}
              onChangeText={setRelationship}
              placeholder="Ex: Son, Daughter, Friend, etc."
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number</Text>
            <TextInput
              style={styles.input}
              value={phoneNumber}
              onChangeText={(text) => setPhoneNumber(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={styles.input}
              value={email}
              onChangeText={setEmail}
              placeholder="example@email.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              textContentType="emailAddress"
            />
          </View>

          {/* Additional Information Toggle */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setShowAdditionalInfo(!showAdditionalInfo)}
            >
              <View style={[styles.toggle, showAdditionalInfo && styles.toggleActive]}>
                <View style={[styles.toggleThumb, showAdditionalInfo && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Add additional information about your caregiver?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              Include details about their location and any special notes.
            </Text>
          </View>

          {showAdditionalInfo && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>State</Text>
                <TouchableOpacity
                  style={styles.input}
                  onPress={() => setShowStateModal(true)}
                >
                  <Text style={[styles.inputText, !state && styles.placeholder]}>
                    {state || 'Select State'}
                  </Text>
                </TouchableOpacity>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Notes</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={notes}
                  onChangeText={setNotes}
                  placeholder="Enter any additional notes about the caregiver"
                  placeholderTextColor="#A9A9A9"
                  multiline
                  numberOfLines={4}
                />
              </View>
            </>
          )}

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {saving ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSave}
                >
                  <Text style={styles.submitButtonText}>
                    {isEditing ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <Modal
        visible={showStateModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowStateModal(false)}
        statusBarTranslucent
        hardwareAccelerated={true} // ← ADDED THIS
      >
        <View style={styles.modalOverlay}>
          {/* This handles the transparent dimmed background and closes the modal */}
          <TouchableOpacity
            style={{ flex: 1 }}
            activeOpacity={1}
            onPress={() => setShowStateModal(false)}
          />

          {/* This is the actual modal content that should stay visible */}
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select State</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowStateModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {US_STATES.map((stateOption) => (
                <TouchableOpacity
                  key={stateOption}
                  style={styles.stateItem}
                  onPress={() => handleStateSelect(stateOption)}
                >
                  <Text style={styles.stateItemText}>{stateOption}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
        borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  placeholder: {
    color: '#A9A9A9',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});
